import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { Copy, ExternalLink, Loader2, Upload } from "lucide-react";
import WhatsAppIcon from "../landing/WhatsAppIcon";
import { openWhatsApp } from "../../utils/whatsappLinks";
import { WHATSAPP_NUMBER } from "../landing/constants";
import { useI18n } from "../../i18n";

const API = "/api";

// iter64 corte 3 — Mercado Pago LINK flow (no webhook; matching via payment_ref).
// Link comes from /api/payments/mp/start (admin-configurable), never hardcoded.
export default function MercadoPagoFlow({ showPenNote = false, onUploadVoucher = null, months = null }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.post(`${API}/payments/mp/start${months ? `?months=${months}` : ""}`);
        setData(r.data);
      } catch (_e) {
        setError(true);
      }
    })();
  }, [months]);

  const copyRef = () => {
    navigator.clipboard.writeText(data.payment_ref);
    toast.success(t("mp.ref_copied"));
  };

  const openLink = async () => {
    window.open(data.mp_link_url, "_blank", "noopener");
    setOpened(true);
    try { await axios.post(`${API}/payments/mp/link-opened`, { payment_ref: data.payment_ref }); } catch (_e) { /* log only */ }
  };

  const confirmReturn = async (via) => {
    try { await axios.post(`${API}/payments/mp/return`, { payment_ref: data.payment_ref, via }); } catch (_e) { /* log only */ }
    if (via === "whatsapp") {
      openWhatsApp(WHATSAPP_NUMBER, t("mp.wa_message", { email: data.email || "", ref: data.payment_ref }));
    } else if (onUploadVoucher) {
      onUploadVoucher();
    }
  };

  if (error) {
    return <p className="text-red-500 text-sm" data-testid="mp-error">{t("mp.error")}</p>;
  }
  if (!data) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm py-4" data-testid="mp-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("mp.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="mp-flow">
      {/* Big reference */}
      <div className="bg-sky-50 border-2 border-sky-200 rounded-2xl p-5 text-center" data-testid="mp-ref-box">
        <p className="text-xs uppercase tracking-wide text-sky-700 font-semibold mb-1">{t("mp.your_ref")}</p>
        <div className="flex items-center justify-center gap-3">
          <span className="font-mono font-bold text-3xl sm:text-4xl text-slate-900 tracking-widest" data-testid="mp-ref-value">
            {data.payment_ref}
          </span>
          <button type="button" onClick={copyRef} className="text-slate-400 hover:text-slate-700" title={t("mp.copy_ref")} data-testid="mp-ref-copy">
            <Copy className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sky-800 text-sm mt-2">{t("mp.ref_instruction")}</p>
      </div>

      {showPenNote && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-amber-800 text-sm" data-testid="mp-pen-note">
          {t("mp.pen_note")}
        </div>
      )}

      {!opened ? (
        <div>
          <Button
            type="button"
            onClick={openLink}
            className="w-full h-12 rounded-full bg-[#009EE3] hover:bg-[#0087C7] text-white font-bold"
            data-testid="mp-go-pay-btn"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            {t("mp.go_pay")}
          </Button>
          <p className="text-center text-xs text-slate-500 mt-2" data-testid="mp-new-tab-note">
            {t("mp.opens_new_tab", { pen: `${data.pen_symbol || ""} ${data.amount}` })}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="mp-already-paid">
          <h4 className="font-bold text-slate-900 text-lg mb-1">{t("mp.already_paid")}</h4>
          <p className="text-slate-600 text-sm mb-4">{t("mp.already_paid_text", { ref: data.payment_ref })}</p>
          <div className="space-y-3">
            <Button
              type="button"
              onClick={() => confirmReturn("voucher")}
              className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
              data-testid="mp-upload-voucher-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {t("mp.upload_voucher")}
            </Button>
            <Button
              type="button"
              onClick={() => confirmReturn("whatsapp")}
              className="w-full h-11 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold"
              data-testid="mp-whatsapp-btn"
            >
              <WhatsAppIcon className="w-4 h-4 mr-2" />
              {t("mp.send_whatsapp")}
            </Button>
            <button
              type="button"
              onClick={openLink}
              className="w-full text-center text-sm text-slate-500 hover:text-slate-700 underline"
              data-testid="mp-reopen-link"
            >
              {t("mp.reopen_link")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
